import { useEffect, useMemo, useState } from 'react';
import { Icon } from './Icon';
import { eventBus } from '@/shared/EventBus';
import { useGameStore } from '@/ui/store/gameStore';
import { BIOMES_BY_ID } from '@/config/biomes.config';
import { PETS_BY_ID } from '@/config/pets.config';
import { getEdu, getEduBiomeUi } from '@/config/edu';
import { petAssetUrl, biomeLandmarkAssetUrl } from '@/assets/paths';
import type { EduEntry } from '@/domain/types/edu';

/**
 * EduInteractBar — pasek interakcji edukacyjnej (każdy biom `educational`).
 *
 * Wzorowany na `PlazaCommsBar`: rozwijany przycisk, interakcja WYŁĄCZNIE na
 * żądanie dziecka (zero losowych dymków → brak zagracenia ekranu).
 *
 * Przepływ: przycisk → grid stworków, które dziecko MA ZAŁOŻONE (equipped, z
 * bieżącego biomu) → po wyborze stworka akcje:
 *  - `phrases` → dymek nad stworkiem w świecie gry (`edu:say`). W „Świecie Flag"
 *    to powitanie i „dziękuję" w języku kraju; w „Puszczy" odgłos zwierzęcia,
 *    ostrzeżenie o grzybie albo cecha rozpoznawcza rośliny — pasek jest na to
 *    obojętny, po prostu renderuje listę z danych.
 *  - Ciekawostka → czytelna karta (krótki tekst, duży font),
 *  - `landmark` → karta z obrazkiem (zabytek kraju / prawdziwy wygląd gatunku).
 *
 * Wszystkie napisy biorą się z `EDU_BIOME_UI` (`src/config/edu/index.ts`) —
 * komponent NIE zna nazw konkretnych biomów.
 *
 * Widoczny tylko gdy gracz jest w świecie (`activeScene==='world'`) i biom jest
 * `educational`. Nie dotyka żadnego istniejącego okna ani modelu peta.
 */

interface OwnedEduPet {
  defId: string;
  namePl: string;
  iconSrc: string;
  edu: EduEntry;
}

type CardState =
  | { kind: 'fact'; title: string; text: string }
  | { kind: 'landmark'; title: string; imageSrc: string; caption: string }
  | null;

export function EduInteractBar(): JSX.Element | null {
  const activeScene = useGameStore((s) => s.activeScene);
  const currentBiomeId = useGameStore((s) => s.currentBiomeId);
  const petInventory = useGameStore((s) => s.petInventory);
  const equippedUids = useGameStore((s) => s.equippedUids);
  const openModal = useGameStore((s) => s.openModal);
  const hatchPhase = useGameStore((s) => s.hatchPhase);
  const isTeleporting = useGameStore((s) => s.isTeleporting);

  const [expanded, setExpanded] = useState(false);
  const [selectedDefId, setSelectedDefId] = useState<string | null>(null);
  const [card, setCard] = useState<CardState>(null);

  const isEduWorld = BIOMES_BY_ID[currentBiomeId]?.educational === true;
  // Napisy per biom edukacyjny (flagi mówią „flaga", puszcza „mieszkaniec").
  const ui = getEduBiomeUi(currentBiomeId);

  const hidden =
    activeScene !== 'world' ||
    !isEduWorld ||
    openModal !== null ||
    hatchPhase !== null ||
    isTeleporting;

  // Stworki posiadane przez gracza, należące do bieżącego biomu edukacyjnego.
  const ownedEduPets = useMemo<OwnedEduPet[]>(() => {
    const seen = new Set<string>();
    const result: OwnedEduPet[] = [];
    for (const inst of petInventory) {
      if (seen.has(inst.defId)) continue;
      const def = PETS_BY_ID[inst.defId];
      const edu = getEdu(inst.defId);
      if (!def || !edu || def.origin.biomeId !== currentBiomeId) continue;
      seen.add(inst.defId);
      result.push({
        defId: inst.defId,
        namePl: def.name.pl,
        iconSrc: petAssetUrl(def.art.textureKey, currentBiomeId),
        edu,
      });
    }
    return result.sort((a, b) => a.namePl.localeCompare(b.namePl, 'pl'));
  }, [petInventory, currentBiomeId]);

  // defId-y stworków, których PRZYNAJMNIEJ JEDNA sztuka jest założona (equipped).
  // Tylko takie stworki są fizycznie w świecie i mogą „mówić" dymkiem.
  const equippedDefIds = useMemo<Set<string>>(() => {
    const equippedSet = new Set(equippedUids);
    const ids = new Set<string>();
    for (const inst of petInventory) {
      if (equippedSet.has(inst.uid)) ids.add(inst.defId);
    }
    return ids;
  }, [petInventory, equippedUids]);

  // Gotowe do rozmowy = posiadane (z tego biomu) ∩ założone na gracza.
  const equippedEduPets = useMemo<OwnedEduPet[]>(
    () => ownedEduPets.filter((f) => equippedDefIds.has(f.defId)),
    [ownedEduPets, equippedDefIds],
  );

  // Reset stanu gdy pasek znika (zmiana sceny/biomu, otwarcie modala).
  useEffect(() => {
    if (hidden) {
      setExpanded(false);
      setSelectedDefId(null);
      setCard(null);
    }
  }, [hidden]);

  // Jeśli wybrany stworek zniknął z założonych (sprzedany/zdjęty) — wróć do gridu.
  useEffect(() => {
    if (selectedDefId && !equippedEduPets.some((f) => f.defId === selectedDefId)) {
      setSelectedDefId(null);
    }
  }, [equippedEduPets, selectedDefId]);

  if (hidden) return null;

  const selected = selectedDefId
    ? equippedEduPets.find((f) => f.defId === selectedDefId) ?? null
    : null;

  const sayPhrase = (defId: string, native: string, pl: string): void => {
    eventBus.emit('edu:say', { petDefId: defId, native, pl });
  };

  return (
    <>
      {/* Czytelna karta (ciekawostka / zabytek) — nakładka na wierzchu. */}
      {card && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 pointer-events-auto font-game"
          onClick={() => setCard(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-3xl p-5 shadow-2xl w-full max-w-sm flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-meadow-700 text-center">{card.title}</h3>
            {card.kind === 'landmark' && (
              <div className="w-full rounded-2xl bg-meadow-50 border border-meadow-200 overflow-hidden flex items-center justify-center min-h-[180px]">
                <img
                  src={card.imageSrc}
                  alt={card.caption}
                  className="max-h-60 w-auto object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            <p className="text-lg leading-relaxed text-gray-800 text-center">
              {card.kind === 'fact' ? card.text : card.caption}
            </p>
            <button
              onClick={() => setCard(null)}
              className="mt-1 rounded-xl bg-meadow-500 hover:bg-meadow-700 text-white font-bold px-6 py-2 active:scale-95 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="absolute bottom-4 left-1/2 z-50 -translate-x-1/2 pointer-events-auto font-game">
        {expanded && (
          <div className="mb-2 w-[min(92vw,400px)] rounded-2xl bg-white/95 p-3 shadow-xl">
            {selected === null ? (
              // ── Tryb wyboru stworka (tylko ZAŁOŻONE pety tego biomu) ──
              equippedEduPets.length === 0 ? (
                <div className="rounded-xl border border-meadow-200 bg-meadow-50 px-3 py-3 text-sm font-bold text-meadow-700 text-center">
                  {ownedEduPets.length === 0 ? ui.emptyOwnedHint : ui.emptyEquippedHint}
                </div>
              ) : (
                <>
                  <div className="mb-2 text-sm font-extrabold text-meadow-900">{ui.pickerTitle}</div>
                  <div className="grid grid-cols-3 gap-1.5 max-h-[52vh] overflow-y-auto pr-1">
                    {equippedEduPets.map((f) => (
                      <button
                        key={f.defId}
                        type="button"
                        onClick={() => setSelectedDefId(f.defId)}
                        className="flex flex-col items-center gap-1 rounded-xl border border-meadow-200 bg-white px-1.5 py-2 shadow-sm transition hover:bg-meadow-50 active:scale-95"
                      >
                        <Icon src={f.iconSrc} fallback="?" alt={f.namePl} className="h-12 w-12" />
                        <span className="text-[11px] font-bold text-meadow-900 text-center leading-tight">
                          {f.namePl}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )
            ) : (
              // ── Tryb akcji (po wyborze stworka) ──
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedDefId(null)}
                    aria-label={ui.backAriaLabel}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-meadow-200 bg-meadow-50 shadow-sm transition active:scale-95"
                  >
                    <Icon src="/ui/ui_arrow_left.webp" fallback="<" alt="wróć" className="h-6 w-6" />
                  </button>
                  <Icon src={selected.iconSrc} fallback="?" alt={selected.namePl} className="h-10 w-10" />
                  <span className="text-base font-extrabold text-meadow-900">{selected.namePl}</span>
                </div>

                {/* `phrases` (powitanie / odgłos / ostrzeżenie) → dymek nad stworkiem */}
                <div className="grid grid-cols-2 gap-1.5">
                  {selected.edu.phrases.map((p) => (
                    <button
                      key={p.labelPl}
                      type="button"
                      onClick={() => sayPhrase(selected.defId, p.native, p.pl)}
                      className="rounded-xl border border-blue-200 bg-blue-50 px-2 py-2 text-sm font-bold text-blue-800 shadow-sm transition hover:bg-white active:scale-95"
                    >
                      {p.labelPl}
                    </button>
                  ))}
                </div>

                {/* Ciekawostka + karta obrazkowa (`landmark`) → czytelna nakładka */}
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    onClick={() =>
                      setCard({
                        kind: 'fact',
                        title: selected.namePl,
                        text: selected.edu.funFact.pl,
                      })
                    }
                    className="rounded-xl border border-amber-200 bg-amber-50 px-2 py-2 text-sm font-bold text-amber-800 shadow-sm transition hover:bg-white active:scale-95"
                  >
                    Ciekawostka
                  </button>
                  {selected.edu.landmark && (
                    <button
                      type="button"
                      onClick={() =>
                        setCard({
                          kind: 'landmark',
                          title: selected.namePl,
                          imageSrc: biomeLandmarkAssetUrl(
                            selected.edu.landmark!.imageKey,
                            currentBiomeId,
                          ),
                          caption: selected.edu.landmark!.caption.pl,
                        })
                      }
                      className="rounded-xl border border-rose-200 bg-rose-50 px-2 py-2 text-sm font-bold text-rose-800 shadow-sm transition hover:bg-white active:scale-95"
                    >
                      {ui.landmarkLabel}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Zamknij naukę' : 'Otwórz naukę'}
          className="flex h-16 min-w-[168px] items-center justify-center gap-2 rounded-full bg-white/95 px-4 shadow-xl transition active:scale-95"
        >
          <Icon src="/ui/ui_btn_comms.webp" fallback="?" alt="nauka" className="h-10 w-10" />
          <span className="text-sm font-extrabold text-meadow-900">{ui.barLabel}</span>
        </button>
      </div>
    </>
  );
}
